// Global size-guide slide-out drawer.
//
// Any link/button with [data-size-guide-open] opens the shared drawer
// (#uac-size-guide-drawer) and loads the content of its target size-guide page
// (the element's href, or [data-size-guide-url]). Behaviour is delegated on the
// document so it works everywhere the link can appear — the product page and,
// crucially, inside the quick-add modal, where a block's own inline script is
// never executed. The href stays a real page URL, so with JS disabled the link
// simply navigates to the size-guide page.
(() => {
  const DRAWER_ID = 'uac-size-guide-drawer';
  const cache = new Map();
  let closing = false;

  const getDrawer = () => document.getElementById(DRAWER_ID);

  const open = () => {
    const d = getDrawer();
    if (!d || d.open) return;
    d.showModal();
    requestAnimationFrame(() => d.classList.add('is-open'));
  };

  const close = () => {
    const d = getDrawer();
    if (!d || closing || !d.open) return;
    closing = true;
    d.classList.remove('is-open');
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      d.removeEventListener('transitionend', onEnd);
      d.close();
      closing = false;
    };
    const onEnd = (e) => {
      if (e.target === d && e.propertyName === 'transform') finish();
    };
    d.addEventListener('transitionend', onEnd);
    setTimeout(finish, 500);
  };

  const extractContent = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const src =
      doc.querySelector('rte-formatter') ||
      doc.querySelector('#MainContent .rte') ||
      doc.querySelector('.rte');
    const title = doc.querySelector('#MainContent h1, .main-page__title, h1');
    return {
      content: src ? src.innerHTML.trim() : '',
      title: title ? title.textContent.trim() : '',
    };
  };

  const loadAndOpen = async (url, explicitTitle) => {
    const d = getDrawer();
    if (!d) return false;
    const contentEl = d.querySelector('[data-size-guide-content]');
    const titleEl = d.querySelector('[data-size-guide-title]');

    if (explicitTitle && titleEl) titleEl.textContent = explicitTitle;

    if (cache.has(url)) {
      const cached = cache.get(url);
      if (contentEl) contentEl.innerHTML = cached.content || '<p>See our size guide.</p>';
      if (!explicitTitle && titleEl && cached.title) titleEl.textContent = cached.title;
      open();
      return true;
    }

    if (contentEl) contentEl.innerHTML = '<p class="size-guide-drawer__loading">Loading…</p>';
    open();

    try {
      const res = await fetch(url, { headers: { 'X-Requested-With': 'fetch' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = extractContent(await res.text());
      cache.set(url, data);
      if (!d.open) return true;
      if (contentEl) contentEl.innerHTML = data.content || '<p>See our size guide.</p>';
      if (!explicitTitle && titleEl && data.title) titleEl.textContent = data.title;
      return true;
    } catch (err) {
      // Fetch failed — fall back to navigating to the page.
      close();
      window.location.href = url;
      return false;
    }
  };

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-size-guide-open]');
    if (opener) {
      const url = opener.getAttribute('href') || opener.dataset.sizeGuideUrl;
      if (!url || url === '#') return;
      e.preventDefault();
      loadAndOpen(url, opener.dataset.sizeGuideTitle);
      return;
    }
    if (e.target.closest('[data-size-guide-close]')) {
      close();
      return;
    }
    const d = getDrawer();
    if (d && e.target === d) close(); // click on the backdrop area
  });

  document.addEventListener('cancel', (e) => {
    if (e.target && e.target.id === DRAWER_ID) {
      e.preventDefault();
      close();
    }
  });
})();
